import addDays from 'date-fns/addDays'
import eachDayOfInterval from 'date-fns/eachDayOfInterval'
import isBefore from 'date-fns/isBefore'
import subDays from 'date-fns/subDays'

import {
  BookingIssue,
  HomeDateOccupancyStatus,
  IDate,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { formatDate, parseISODate } from '@avantstay/backoffice-core'

import { PeriodGroup } from '@new/__global/components/TapeChart/PeriodRenderer/PeriodRenderer.types'
import { DatePrices } from '@new/domains/bookings/ModifyBookingSidePanel/ModifyBookingSidePanel.definitions'

enum DateRelationToRangePeriod {
  start = 'START',
  end = 'END',
}

export const shouldFadeForFilters = (
  item: PeriodGroup,
  isActive: boolean,
  filteredIssues?: BookingIssue[],
  filteredPaymentStatus?: string[],
  depositsOnHold?: boolean,
) => {
  // Check if has Booking Issue enabled filter on period
  const hasBookingIssueFiltered = () => {
    const periodIssues = item.issues
    if (!filteredIssues || !periodIssues || periodIssues?.length === 0) return false
    const bookingHasFilteredIssues =
      filteredIssues?.filter(element => periodIssues.includes(element)).length > 0
    return bookingHasFilteredIssues
  }

  // Check if has Payment Status enabled filter on period
  const periodHasPaymentStatusFiltered = () => {
    const bookingPaymentStatus = item.paymentBalanceStatus
    if (!filteredPaymentStatus || !bookingPaymentStatus) return false
    return filteredPaymentStatus.includes(bookingPaymentStatus)
  }

  const fadeOnBookingIssuesFilter =
    filteredIssues && filteredIssues.length > 0 && !hasBookingIssueFiltered() && !isActive

  const fadeOnDepositsOnHoldFilter =
    item.periods[0]?.securityDepositStatus !== false && depositsOnHold && !isActive

  const shouldFadeOnPaymentStatusFilter = filteredPaymentStatus && !periodHasPaymentStatusFiltered()

  return fadeOnBookingIssuesFilter || shouldFadeOnPaymentStatusFilter || fadeOnDepositsOnHoldFilter
}

export const isDayAvailable = (
  date: IDate,
  datesInTapechartRange: DatePrices[],
  dateRelationToRangePeriod?: DateRelationToRangePeriod,
) => {
  const day = datesInTapechartRange.find(d => formatDate(d.date) === formatDate(date))
  const { checkIn, checkOut, checkOutAndCheckIn, occupied } = HomeDateOccupancyStatus
  let isOccupiedDate = day?.status === occupied || day?.status === checkOutAndCheckIn

  if (
    dateRelationToRangePeriod === DateRelationToRangePeriod.start ||
    dateRelationToRangePeriod === DateRelationToRangePeriod.end
  ) {
    const prevDay = datesInTapechartRange.find(
      d => formatDate(d.date) === formatDate(subDays(parseISODate(date), 1)),
    )
    const nextDay = datesInTapechartRange.find(
      d => formatDate(d.date) === formatDate(addDays(parseISODate(date), 1)),
    )

    const isUnavailableStartDate =
      dateRelationToRangePeriod === DateRelationToRangePeriod.start &&
      day?.status === checkIn &&
      nextDay?.status === checkOut
    const isUnavailableEndDate =
      dateRelationToRangePeriod === DateRelationToRangePeriod.end &&
      day?.status === checkOut &&
      prevDay?.status === checkIn

    if (isUnavailableStartDate || isUnavailableEndDate) isOccupiedDate = true
  }

  if (!day || isOccupiedDate) return false
  return true
}

const forbiddenStatus = [
  HomeDateOccupancyStatus.checkOutAndCheckIn,
  HomeDateOccupancyStatus.occupied,
]

interface GetBlockedDatesProps {
  allDatePrices: DatePrices[]
  selectedPeriod?: { start: string; end: string }
  originalPeriod?: { start: IDate; end: IDate }
}
export const getBlockedDates = ({
  allDatePrices,
  selectedPeriod,
  originalPeriod,
}: GetBlockedDatesProps) => {
  if (!originalPeriod || !selectedPeriod)
    return allDatePrices.map(datePrice => formatDate(datePrice.date))
  const originalRange = eachDayOfInterval({
    start: parseISODate(originalPeriod.start),
    end: parseISODate(originalPeriod.end),
  }).map(date => formatDate(date))

  const blockedDates = allDatePrices
    .filter(datePrice => {
      const isWithinOriginalBookingRange = originalRange.includes(formatDate(datePrice.date))

      const isCheckInBlocked =
        isBefore(parseISODate(datePrice.date), parseISODate(selectedPeriod.start)) &&
        datePrice.status.includes(HomeDateOccupancyStatus.checkIn)

      return isWithinOriginalBookingRange
        ? false
        : forbiddenStatus.includes(datePrice.status) || isCheckInBlocked
    })
    .map(datePrice => datePrice.date as string)

  return blockedDates
}

export const isRangeAvailable = (
  { start, end }: { start: IDate; end: IDate },
  datesInTapechartRange: DatePrices[],
) => {
  if (!start || !end) return false

  const daysInRange = eachDayOfInterval({ start: parseISODate(start), end: parseISODate(end) })
  const daysBetweenCIandCO = daysInRange.slice(1, -1)
  const endDateAvailable = isDayAvailable(end, datesInTapechartRange, DateRelationToRangePeriod.end)
  const startDateAvailable = isDayAvailable(
    start,
    datesInTapechartRange,
    DateRelationToRangePeriod.start,
  )
  const datesBetweenCIandCOAreAvailable =
    daysBetweenCIandCO.filter(d => !isDayAvailable(d, datesInTapechartRange)).length === 0

  return endDateAvailable && startDateAvailable && datesBetweenCIandCOAreAvailable
}
